import type ApplicationModel from 'components/framework/ApplicationModel';
import { camelize } from 'inflected';
import type CardLayout from '../CardLayout';
import type CardView from '../CardView';
import type RecordLayout from '../RecordLayout';

const CARD_LAYOUT_HIERARCHY_TYPES = [
  'CardLayout',
  'RecordLayout',
  'CardView',
  'CardRow',
  'CardGroup',
  'CardField',
];

const CARD_LAYOUT_HIERARCHY_PARENTS = {
  CardStyling: 'CardView',
};

function findParentForRecord(parentType: string, record: ApplicationModel) {
  if (
    !CARD_LAYOUT_HIERARCHY_TYPES.includes(record.typename) &&
    !(record.typename in CARD_LAYOUT_HIERARCHY_PARENTS)
  )
    return;

  while (record) {
    if (record.typename === parentType) return record;

    record = record._parent;
  }
}

export interface HasCardLayoutHierarchy {
  cardLayout: CardLayout;
  cardView: CardView;
  recordLayout: RecordLayout;
  modelClass: RecordLayout['modelClass'];
}

/**
 * For all models that are used in the CardLayout hierarchy, this will automatically add
 * getters to access any ancestors. For example, on an instance of a `CardField`, you can
 * call any of the following getters:
 *
 *   1. cardLayout.cardGroup
 *   2. cardLayout.cardRow
 *   3. cardLayout.cardView
 *   4. cardLayout.recordLayout
 *   5. cardLayout.cardLayout
 */
export function HasCardLayoutHierarchy(layoutClass: any): any {
  // Define getters for all ancestors
  let className = layoutClass.typename;
  let hierarchyIndex;

  if (className in CARD_LAYOUT_HIERARCHY_PARENTS) {
    className = CARD_LAYOUT_HIERARCHY_PARENTS[className];
    hierarchyIndex = CARD_LAYOUT_HIERARCHY_TYPES.indexOf(className) + 1;
  } else {
    hierarchyIndex = CARD_LAYOUT_HIERARCHY_TYPES.indexOf(className);
  }

  if (hierarchyIndex > -1) {
    const ancestors = CARD_LAYOUT_HIERARCHY_TYPES.slice(0, hierarchyIndex);

    ancestors.forEach(name => {
      Object.defineProperty(layoutClass.prototype, camelize(name, false), {
        get: function () {
          return findParentForRecord(name, this);
        },
        configurable: true,
      });
    });

    // Add modelClass getter as well
    if (hierarchyIndex > 1) {
      Object.defineProperty(layoutClass.prototype, 'modelClass', {
        get: function (this: HasCardLayoutHierarchy) {
          return this.recordLayout?.modelClass;
        },
        configurable: true,
      });
    }
  }

  return layoutClass;
}
